import { useId, useState, useEffect, type FormEvent } from 'react';
import { Button } from '@domcrm/ui';
import { postAuthMeFeedback, type AuthMeFeedbackPayload } from '../../entities/me/me.api';
import { isStaffPlatformMode } from '../../shared/access/staff-platform-mode';
import { userFacingActionError } from '../../shared/lib/staff-user-facing-error';
import { Field } from '../../shared/ui/Field';
import { ModalDialog } from '../../shared/ui/ModalDialog';
import { StaffTrainerMarkdownField } from '../../shared/ui/StaffTrainerMarkdownField';
import { Switch } from '../../shared/ui/Switch';
import { useStaffToast } from '../../shared/ui/StaffToast';
import { useAuth } from '../../shared/auth/use-auth';

export type StaffContactDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** После успешной отправки (например обновить журнал на `/feedback`). */
  onSubmitted?: () => void;
  /** Предзаполненные поля (например при отправке багрепорта ИИ-помощника). */
  initialValues?: Partial<AuthMeFeedbackPayload>;
  /** Кастомный заголовок окна. */
  title?: string;
  /** Кастомное описание под заголовком. */
  description?: string;
};

const MAX_BODY_LEN = 1000;

const DEFAULT_FORM: AuthMeFeedbackPayload = {
  recipient: 'developers',
  category: 'improvement',
  body_md: '',
  is_anonymous: false
};

export function StaffContactDialog({
  open,
  onOpenChange,
  onSubmitted,
  initialValues,
  title,
  description
}: StaffContactDialogProps) {
  const formId = useId();
  const toast = useStaffToast();
  const { apiClient, me, impersonateOrgId } = useAuth();
  const platformSuperadmin = me != null && isStaffPlatformMode(me, impersonateOrgId);
  /** Тема «Смена главного админа» доступна только действующему главному админу в контексте организации. */
  const canMainAdminChange = (me?.role ?? '').trim().toLowerCase() === 'org_main_admin' && !platformSuperadmin;
  const [form, setForm] = useState<AuthMeFeedbackPayload>(DEFAULT_FORM);
  const [bodyError, setBodyError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const initialCategory = initialValues?.category ?? DEFAULT_FORM.category;
      const category =
        initialCategory === 'main_admin_change' && !canMainAdminChange
          ? DEFAULT_FORM.category
          : initialCategory;
      setForm({
        recipient: category === 'main_admin_change'
          ? 'developers'
          : initialValues?.recipient ?? DEFAULT_FORM.recipient,
        category,
        body_md: initialValues?.body_md ?? DEFAULT_FORM.body_md,
        is_anonymous: initialValues?.is_anonymous ?? DEFAULT_FORM.is_anonymous
      });
      setBodyError(undefined);
    }
  }, [open, initialValues, canMainAdminChange]);

  function close(): void {
    onOpenChange(false);
    setForm(DEFAULT_FORM);
    setBodyError(undefined);
    setSubmitting(false);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const text = form.body_md.trim();
    if (!text) {
      setBodyError('Введите текст отзыва');
      return;
    }
    if (text.length > MAX_BODY_LEN) {
      setBodyError(`Не более ${MAX_BODY_LEN} символов`);
      return;
    }

    setBodyError(undefined);
    setSubmitting(true);
    try {
      const isMainAdminChange = form.category === 'main_admin_change';
      await postAuthMeFeedback(apiClient, {
        ...form,
        recipient: isMainAdminChange || platformSuperadmin ? 'developers' : form.recipient,
        body_md: text
      });
      toast({
        variant: 'success',
        message: isMainAdminChange
          ? 'Заявление отправлено'
          : form.category === 'complaint'
            ? 'Отчёт об ошибке отправлен разработчикам'
            : 'Отзыв отправлен'
      });
      onSubmitted?.();
      close();
    } catch (err) {
      toast({ variant: 'error', message: userFacingActionError(err) });
    } finally {
      setSubmitting(false);
    }
  }

  const isMainAdminChange = form.category === 'main_admin_change';
  const dialogTitle = isMainAdminChange
    ? 'Заявление на смену главного администратора'
    : title ?? (form.category === 'complaint' ? 'Сообщить об ошибке' : 'Оставить отзыв');
  const dialogDescription = isMainAdminChange
    ? 'Опишите, на кого сменить главного администратора (ФИО, телефон или email), и коротко причину. Заявление уйдёт суперадмину платформы.'
    : description ??
      (platformSuperadmin
        ? 'Текст в Markdown, не более 1000 символов. Отзыв уходит в очередь поддержки платформы.'
        : 'Текст в Markdown, не более 1000 символов. Отзыв к платформе уходит в очередь поддержки; сообщение администратору организации сохраняется в CRM.');

  return (
    <ModalDialog
      open={open}
      onOpenChange={(next) => {
        if (next) onOpenChange(true);
        else close();
      }}
      title={dialogTitle}
      description={dialogDescription}
      className="max-h-[min(90vh,900px)] overflow-y-auto sm:max-w-xl"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={close} disabled={submitting}>
            Отменить
          </Button>
          <Button type="submit" form={formId} disabled={submitting}>
            {submitting ? 'Отправляем…' : 'Отправить'}
          </Button>
        </>
      }
    >
      <form id={formId} className="space-y-3" onSubmit={(e) => void onSubmit(e)}>
        <Field label="Тема">
          <select
            className="h-9 w-full rounded-[var(--ui-radius-control)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-background))] px-3 text-sm text-[hsl(var(--ui-foreground))] outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]"
            value={form.category}
            onChange={(e) => {
              const category = e.target.value as AuthMeFeedbackPayload['category'];
              setForm((prev) => ({
                ...prev,
                category,
                ...(category === 'main_admin_change' ? { recipient: 'developers' as const } : {})
              }));
            }}
          >
            <option value="improvement">Предложение улучшения</option>
            <option value="complaint">Жалоба / ошибка</option>
            <option value="freeform">Свободная форма</option>
            {canMainAdminChange ? <option value="main_admin_change">Смена главного администратора</option> : null}
          </select>
        </Field>
        {platformSuperadmin || isMainAdminChange ? null : (
          <Field label="Адресат">
            <select
              className="h-9 w-full rounded-[var(--ui-radius-control)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-background))] px-3 text-sm text-[hsl(var(--ui-foreground))] outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]"
              value={form.recipient}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, recipient: e.target.value as AuthMeFeedbackPayload['recipient'] }))
              }
            >
              <option value="developers">Платформа (разработчикам)</option>
              <option value="org_admin">Администратор организации</option>
            </select>
          </Field>
        )}
        <div>
          <StaffTrainerMarkdownField
            label="Текст отзыва"
            value={form.body_md}
            onChange={(next) => {
              setForm((prev) => ({ ...prev, body_md: next }));
              if (bodyError) setBodyError(undefined);
            }}
            placeholder={
              isMainAdminChange
                ? 'Например: прошу назначить главным администратором Петра Иванова, телефон +7 999 123-45-67. Причина: передаю управление студией.'
                : form.category === 'complaint'
                  ? 'Опишите ошибку или что пошло не так…'
                  : 'Опишите вопрос или предложение…'
            }
            editorHeight={220}
          />
          <div className="mt-1 flex justify-between gap-3 text-xs text-[hsl(var(--ui-muted-foreground))]">
            <span className="text-amber-700 dark:text-amber-400">{bodyError ?? ''}</span>
            <span>
              {form.body_md.length}/{MAX_BODY_LEN}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-[var(--ui-radius)] border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted)/0.45)] px-3 py-2">
          <span className="text-sm text-[hsl(var(--ui-foreground))]">Отправить анонимно</span>
          <Switch
            checked={form.is_anonymous}
            onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_anonymous: checked }))}
            aria-label="Отправить анонимно"
          />
        </div>
      </form>
    </ModalDialog>
  );
}
